import {
  Box,
  Button,
  Container,
  Form,
  Grid,
  Header,
  Input,
  Modal,
  Select,
  SelectProps,
  SpaceBetween,
} from '@amzn/awsui-components-core';
import React, { useState } from 'react';
import { ContentNode, ContentType } from '@amzn/awscat-pattern-library-service-model';
import constants from '../../../common/constants';
import { contentTypeDisplayNames } from '../../utils/DisplayNames';
import { ContentTypeToPageTypeMap, TemplatePageType } from '../../utils/SelectableOptions';
import { wordify } from '../../../common/NumberUtils';

interface Props {
  fromSections: ContentNode[];
  fromContentType: ContentType;
  onCancel?: () => void;
  onConfirm?: (pageSectionMap: Map<string, string>) => void;
  toSections?: ContentNode[];
  toContentType: ContentType;
}

const ContentConverterInput: React.FunctionComponent<Props> = (props: Props) => {
  const { fromContentType, fromSections, onCancel, onConfirm, toContentType, toSections } = props;
  const convertFromSinglePage =
    ContentTypeToPageTypeMap.get(fromContentType) === TemplatePageType.SinglePage;
  const convertToSinglePage =
    ContentTypeToPageTypeMap.get(toContentType) === TemplatePageType.SinglePage;
  // @ts-expect-error TS2532 - strict migration
  const fromContentTypeLabel = contentTypeDisplayNames.get(fromContentType).toLocaleLowerCase();
  // @ts-expect-error TS2532 - strict migration
  const toContentTypeLabel = contentTypeDisplayNames.get(toContentType).toLocaleLowerCase();
  const headerTitle = `Change ${fromContentTypeLabel} to ${toContentTypeLabel}`;

  const fromSinglePageNote = `Your ${fromContentTypeLabel} has ${wordify(fromSections.length)} sections that will be migrated into separate pages \
        in the new ${toContentTypeLabel}, as shown in the following table. You can rename the pages after conversion.`;
  const toSinglePageNote = `Your ${fromContentTypeLabel} has ${wordify(fromSections.length)} pages that will be converted to sections in the new, single-page ${toContentTypeLabel}. \
        To complete the conversion, select the section title that you would like to migrate each page to.`;
  const otherNote = `Your ${fromContentTypeLabel} has ${wordify(fromSections.length)} pages that will be migrated into same pages \
        in the new ${toContentTypeLabel}, as shown in the following table. You can rename the pages after conversion.`;
  const headerNote = convertFromSinglePage
    ? fromSinglePageNote
    : convertToSinglePage
      ? toSinglePageNote
      : otherNote;

  const initializeMap = () => {
    const map = new Map<string, string>();
    if (convertToSinglePage && fromSections) {
      fromSections.forEach((node) =>
        map.set(node.id, node.id === 'rootId' ? 'summary' : 'miscellaneous'),
      );
    }
    return map;
  };
  const [pageSectionMap, setPageSectionMap] = useState(initializeMap());

  const onConfirmChange = () => {
    if (onConfirm) {
      onConfirm(pageSectionMap);
    }
  };
  const onDismiss = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const getLabel = (section: string, id: string, label: string) => {
    const key = section + '-' + id;
    return (
      <Box key={key}>
        <Input value={label} readOnly ariaRequired />
      </Box>
    );
  };
  const renderLabelItems = (section: string) => {
    return fromSections.map((contentNode) => getLabel(section, contentNode.id, contentNode.title));
  };

  const leftHeader = <Header variant="h3">{constants.CONTENT_CONVERSION_LEFT_HEADER}</Header>;

  const renderLeftSection = (
    <Container header={leftHeader}>
      <SpaceBetween size="xxxs">{renderLabelItems('left')}</SpaceBetween>
    </Container>
  );

  const setSelectedOption = (detail: SelectProps.ChangeDetail, pageId: string) => {
    const selectedValue = detail.selectedOption.value;
    // @ts-expect-error TS2345 - strict migration
    setPageSectionMap(new Map(pageSectionMap.set(pageId, selectedValue)));
  };

  const getSelectItem = (pageId: string, selectItems: SelectProps.Option[]) => {
    const selectedOption = selectItems.find((item) => item.value === pageSectionMap.get(pageId));
    const key = 'select-' + pageId;
    /* tslint:disable:jsx-no-lambda */
    return (
      <Box key={key}>
        <Select
          // @ts-expect-error TS2322 - strict migration
          selectedOption={selectedOption}
          onChange={(e) => setSelectedOption(e.detail, pageId)}
          options={selectItems}
          selectedAriaLabel="Selected"
          ariaRequired
        />
      </Box>
    );
  };

  const getSelectItems = () => {
    return toSections
      ? toSections.map((contentNode) => {
          const { id, title } = contentNode;
          return { label: title, value: id };
        })
      : [];
  };

  const renderSelectItems = () => {
    const selectItems = getSelectItems();
    return fromSections.map((contentNode) => getSelectItem(contentNode.id, selectItems));
  };

  const renderRightItems = () => {
    return convertToSinglePage ? renderSelectItems() : renderLabelItems('right');
  };

  const rightHeader = <Header variant="h3">{constants.CONTENT_CONVERSION_RIGHT_HEADER}</Header>;

  const renderRightSection = (
    <Container header={rightHeader}>
      <SpaceBetween size="xxxs">{renderRightItems()}</SpaceBetween>
    </Container>
  );

  const renderGrid = (
    <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
      {renderLeftSection}
      {renderRightSection}
    </Grid>
  );

  const formActions = (
    <div className="task-editor-form-actions">
      <Button onClick={onDismiss} variant="link">
        {constants.CANCEL_BUTTON_TEXT}
      </Button>
      <Button onClick={onConfirmChange} variant="primary">
        {constants.CONFIRM_CHANGES_BUTTON_TEXT}
      </Button>
    </div>
  );

  return (
    <div className="form-action">
      <Modal
        className="awsui"
        onDismiss={onDismiss}
        header={headerTitle}
        size="large"
        visible
        closeAriaLabel="Close modal"
      >
        <Form actions={formActions}>
          <Box variant="p" margin={{ bottom: 'm' }}>
            {headerNote}
          </Box>
          {renderGrid}
        </Form>
      </Modal>
    </div>
  );
};

export default ContentConverterInput;
